<script>
  import { layoutMethods, 
           peddlrWalletKycLiteMethods, 
           peddlrWalletKycLiteComputed, 
           exportMethods, 
           erpFixMessages } from "@/state/helpers";
  import flatPickr from "vue-flatpickr-component";
  import "flatpickr/dist/flatpickr.css";
  import Swal from 'sweetalert2';


  /**
   * KYC Activity Log component
   */
  export default {
    page: {
      title: "KYC Activity Log"
    },
    components: {
        flatPickr
    },
    computed: {
      ...peddlrWalletKycLiteComputed,
      ...erpFixMessages
    },
    data() {
      return {
        title: "KYC Activity Log",
        businessId: 0,
        infoType  : null,
        searchValue: {
          dateRange: null,
          sortBy: 'id',
          sortDesc: true,
          currentPage: 1,
          rowsPerPage: 10,
        },
        pageOptions: [10, 25, 50, 100],
        rangeOptions: {
          mode: "range",
        },
        fieldsActivityLogs: [
          { key: 'kycAccountType', sortable: true, label: 'KYC Account Type' },
          { key: 'kycAccountStatus', sortable: true, label: 'KYC Account Status' },
          { key: 'dateVerified', sortable: true, label: 'Date Verified' },
          { key: 'remarks', sortable: false, label: 'Verification Reason' }
        ],
        isLoading: true
      };
    },
    methods: {
      ...layoutMethods,
      ...peddlrWalletKycLiteMethods,
      ...exportMethods,
      async fetchKycActivityLogsList(){
        let requestPayload = {
          ...this.searchValue,
          businessId: this.businessId
        }

        await this.getUserKycActivityLogs({
            ...requestPayload
        }).then(() => {
            
        }).catch((err) => {
            console.error("err", err);
        }).finally(() => {
            this.isLoading = false;
        });
      },
      tableSorting(action) {
        switch (action.sortBy) {
          case 'kycAccountType':
            this.searchValue.sortBy = "kyc_acct_type";
            break;
          case 'kycAccountStatus':
            this.searchValue.sortBy = "kyc_acct_status";
            break;
          case 'dateVerified':
            this.searchValue.sortBy = "date_verified";
            break;
          default:
            break;
        }

        this.searchValue.sortDesc = action.sortDesc;
        this.onSearch('reset');
      },
      async onSearch(state = ''){
          if(state == 'reset'){
            this.searchValue.currentPage = 1;
          }

          await this.fetchKycActivityLogsList();
      },
      async onExport(){
        await this.onSearch('reset');
        if (this.userKycActivityLogsTotal == 0) {
          Swal.fire("Failed!", this.exportNullPrompt, "warning");
          return;
        }

        let params = Object.assign(
            this.searchValue, 
            {
              module: 'user_kyc_activity_logs',
              email: JSON.parse(localStorage.getItem('userData')).email,
              businessId: this.businessId
            });

        delete params.page;
        delete params.rows;

        // Use exportReportExt (API2 -> /v1/others) which routes to the ERPAPI2 TriggersExportModule
        // (triggers/export.js handles module 'user_kyc_activity_logs'). exportReport (API -> /v1/core) does
        // NOT reach that lambda, so the KYC export failed with a blank report name.
        this.exportReportExt(params)
          .then(() => Swal.fire("Success!", `Your request for reports is pending. It will be sent to your email once it's ready.`, "success"))
          .catch(() => Swal.fire("Failed!", `Your request for reports has failed.`, "warning"));

      },
    },
    async mounted(){
      this.businessId = this.$route.params.bid
      this.infoType = this.$route.params.tab;
      this.fetchKycActivityLogsList();
    }
  }
    
</script>


<template>
  <div><!-- outer div-->
    <div class="card-body p-3">
      <h5>KYC Activity Log ({{ userKycActivityLogsTotal }})</h5>

      <div class="d-flex flex-wrap mt-4 mb-2"> <!-- start of Filter, Search, Export -->
      <div class="me-2 ta-right">
        <b-form-group description="Filter by date">
        <flat-pickr
            v-model="searchValue.dateRange"
            :config="rangeOptions"
            placeholder="Select date range"
            class="form-control"
            style="width: 210px"
        ></flat-pickr>
        </b-form-group>
      </div>
      <div class="me-2">
        <a class="btn btn-primary h-fit" @click="onSearch('reset')">
        <i class='bx bx-search bx-fw'></i>Search
        </a>
      </div>
      <div class="me-2">
        <a href="javascript:void(0);" class="btn btn-primary me-2" @click="onExport">
        <i class="uil uil-export me-1"></i>
        Export
        </a>
      </div>
      </div> <!-- end of Filter, Search, Export -->

      <div class="table-responsive mt-3"> <!-- start of Activity Logs table -->
      <div class="table align-middle table-nowrap">
        <b-table :items="userKycActivityLogsList"
                 :fields="fieldsActivityLogs"
                 responsive="sm"
                 :current-page="searchValue.currentPage"
                 :sort-by.sync="searchValue.sortBy"
                 :sort-desc.sync="searchValue.sortDesc"
                 class="table-check"
                 @sort-changed="tableSorting">

          <template #cell(kycAccountType)="data">
            {{ data.item.kycAccountType | formatKycAccountType }}
          </template>

          <template #cell(kycAccountStatus)="data">
            {{ data.item.kycAccountStatus | formatKycAccountStatus }}
          </template>

          <template #cell(dateVerified)="data">
            {{ data.item.dateVerified | customFormatDate }}
          </template>

        </b-table>
      </div>
      </div> <!-- end of Activity Logs table -->

      <div class="row g-0 mb-1 text-center" v-if="isLoading">
        <span>Loading data...</span>
      </div>
      <div class="row g-0 text-center" v-if="!isLoading && userKycActivityLogsTotal == 0">
        <span>No data to display.</span>
      </div>

      <div class="col-sm-12"> <!-- start of table footer (Display #, and Pagination) -->
        <div class="row mt-2">
          <div class="col mt-2">
            <div class="form-group row pull-left" v-if="userKycActivityLogsTotal > 10">
              <label for="staticEmail" class="col-sm-6 col-form-label">Display:</label>
              <div class="col-sm-6">
                  <b-form-group label-for="display-record">
                  <b-form-select class="form-select shadow-none w-fit" 
                                  id="display-record" 
                                  v-model="searchValue.rowsPerPage"
                                  :options="pageOptions" 
                                  @change="onSearch('reset')"
                  ></b-form-select>
                  </b-form-group>
              </div>
            </div>
            <div class="dataTables_paginate paging_simple_numbers float-end" v-if="userKycActivityLogsTotal > searchValue.rowsPerPage">
              <!-- pagination -->
              <b-pagination class="pagination-rounded" 
                              v-model="searchValue.currentPage" 
                              :total-rows="userKycActivityLogsTotal"
                              @input="onSearch()" 
                              :per-page="searchValue.rowsPerPage"
              ></b-pagination>
            </div>
          </div>
        </div>
        </div> <!-- end of table footer (Display #, and Pagination) -->
    </div>

    <!-- <div class="ta-right">
      <a class="btn btn-light h-fit w-sm" @click="closeModal('bv-modal-kyc-activity-log')">
        Close
      </a>
    </div> -->

  </div> <!-- end of outer div-->
</template>


<style>
</style>